import { Check, CirclePlus, Loader2, Plus, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";

import {
  useCreateTagMutation,
  useTagsQuery,
} from "@/modules/mine";
import type { ItemTagData } from "@/modules/mine/types";
import { cn } from "@/shared/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/ui/popover";

const MAX_TAG_LENGTH = 16;

function normalizeTag(tag: string) {
  return tag.trim().replace(/^#+/, "").replace(/\s+/g, " ");
}

function getTagSignature(tagName: string) {
  return normalizeTag(tagName).toLowerCase();
}

interface TagSelectorProps {
  value: ItemTagData[];
  onChange: (tags: ItemTagData[]) => void;
  allowCreate?: boolean;
  className?: string;
  chipClassName?: string;
  portalledDropdown?: boolean;
  popoverAlign?: "start" | "center" | "end";
  placeholder?: string;
  triggerLabel?: string;
  triggerClassName?: string;
}

export function TagSelector({
  value,
  onChange,
  allowCreate = true,
  className,
  chipClassName,
  portalledDropdown = false,
  popoverAlign = "start",
  placeholder = "搜索或创建标签...",
  triggerLabel = "添加标签",
  triggerClassName,
}: TagSelectorProps) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const createTagMutation = useCreateTagMutation();
  const tagsQuery = useTagsQuery();
  const allTags = tagsQuery.data?.tags ?? [];
  const portalContainer = portalledDropdown
    ? null
    : triggerRef.current?.closest(
      "[data-slot='dialog-content']",
    ) as HTMLElement | null;

  const selectedTagIds = useMemo(
    () => new Set(value.map((tag) => tag.id)),
    [value],
  );

  const normalizedInput = normalizeTag(inputValue);
  const inputSignature = getTagSignature(inputValue);
  const matchingTags = allTags.filter((tag) =>
    getTagSignature(tag.name).includes(inputSignature),
  );
  const hasExactMatch = allTags.some(
    (tag) => getTagSignature(tag.name) === inputSignature,
  );
  const canCreateTag = allowCreate && Boolean(normalizedInput) && !hasExactMatch;

  const handleSelectTag = (tag: ItemTagData) => {
    if (!selectedTagIds.has(tag.id)) {
      onChange([...value, tag]);
    }

    setInputValue("");
    setOpen(false);
  };

  const handleCreateAndSelectTag = async () => {
    if (!canCreateTag || createTagMutation.isPending) {
      return;
    }

    const createdTag = await createTagMutation.mutateAsync({
      name: normalizedInput.slice(0, MAX_TAG_LENGTH),
    });

    handleSelectTag(createdTag);
  };

  const handleRemoveTag = (tagId: string) => {
    onChange(value.filter((tag) => tag.id !== tagId));
  };

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {value.map((tag) => (
        <span
          key={tag.id}
          className={cn(
            "inline-flex h-7 items-center gap-1 rounded-md bg-muted px-2 text-xs text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground",
            chipClassName,
          )}
        >
          #{tag.name}
          <button
            aria-label={`删除标签 ${tag.name}`}
            className="rounded-sm text-muted-foreground/60 hover:text-foreground"
            onClick={() => handleRemoveTag(tag.id)}
            type="button"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}

      <Popover modal={portalledDropdown} open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            className={cn(
              "inline-flex h-7 items-center gap-1 rounded-md px-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
              triggerClassName,
            )}
            type="button"
          >
            <CirclePlus className="size-3.5" />
            {triggerLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align={popoverAlign}
          className="p-0"
          container={portalContainer ?? undefined}
          collisionPadding={8}
          sideOffset={8}
        >
          <Command shouldFilter={false}>
            <CommandInput
              maxLength={MAX_TAG_LENGTH}
              onValueChange={setInputValue}
              placeholder={placeholder}
              value={inputValue}
            />
            <CommandList>
              {matchingTags.length === 0 && !canCreateTag ? (
                <CommandEmpty>没有找到标签</CommandEmpty>
              ) : null}
              {matchingTags.length > 0 ? (
                <CommandGroup heading="已有标签">
                  {matchingTags.map((tag) => {
                    const selected = selectedTagIds.has(tag.id);

                    return (
                      <CommandItem
                        className="cursor-pointer"
                        disabled={selected}
                        key={tag.id}
                        onSelect={() => handleSelectTag(tag)}
                        value={tag.name}
                      >
                        <span className="text-muted-foreground/70">#</span>
                        <span className="truncate">{tag.name}</span>
                        {selected ? (
                          <Check className="ml-auto size-3.5 text-muted-foreground" />
                        ) : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ) : null}
              {canCreateTag ? (
                <CommandGroup heading="创建标签">
                  <CommandItem
                    className="cursor-pointer"
                    disabled={createTagMutation.isPending}
                    onSelect={() => void handleCreateAndSelectTag()}
                    value={normalizedInput}
                  >
                    {createTagMutation.isPending ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      <Plus className="size-3.5" />
                    )}
                    <span className="truncate">创建 “{normalizedInput}”</span>
                  </CommandItem>
                </CommandGroup>
              ) : null}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
